import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, type WebPack } from "../lib/api";
import { useAuth } from "../lib/auth";
import { capture } from "../lib/analytics";
import { Header, Spinner, Muted, ErrorText, GildedFrame } from "../components/ui";

// Cold web traffic buys at FULL price with STANDARD credits. The app2web bonus is
// app-only steering (it's funded by the Apple/Google cut we recover, which cold
// web traffic never cost us). The server decides that from a steer token, so this
// page only forwards one if the app put it in the URL.

export function Buy() {
  const { credits, refresh } = useAuth();
  const [params] = useSearchParams();
  const steerToken = params.get("st") ?? undefined;
  const [packs, setPacks] = useState<Record<string, WebPack> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setPacks((await api.packs()).packs);
        capture("webBuyViewed", { context: steerToken ? "app2web" : "web2app" });
      } catch {
        setErr("Couldn't load credit packs. Please refresh.");
      }
      void refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (params.get("canceled")) setErr("Checkout canceled. Nothing was charged.");
  }, [params]);

  async function buy(key: string) {
    setBusy(key);
    setErr(null);
    try {
      const res = await api.checkout(key, steerToken);
      capture("webCheckoutStarted", { packKey: key, context: res.context, credits: res.credits });
      window.location.href = res.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't start checkout.");
      setBusy(null);
    }
  }

  if (!packs) return <Spinner />;

  return (
    <>
      <Header kicker="Credits" title="Get credits" />
      <Muted className="mb-5">
        One credit turns one selfie into a full gallery of you in the biblical world. No subscription.
        {credits ? ` You have ${credits.available} right now.` : ""}
      </Muted>

      <div className="grid gap-3 sm:grid-cols-3">
        {(["one", "three", "ten"] as const).map((key) => {
          const p = packs[key];
          if (!p) return null;
          // Only show a bonus when the app actually steered us here.
          const bonus = steerToken && p.bonus > 0 ? p.bonus : 0;
          return (
            <button key={key} onClick={() => void buy(key)} disabled={!!busy} className="text-left">
              <GildedFrame className="h-full transition hover:border-gold/60">
                <div className="p-4">
                  <div className="font-serif text-2xl text-ivory">
                    {p.credits + bonus}
                    <span className="ml-1 text-base text-warmStone">credits</span>
                  </div>
                  {bonus > 0 && (
                    <div className="mt-1 inline-block rounded-full bg-gold/15 px-2 py-0.5 text-[11px] text-goldBright">
                      +{bonus} free
                    </div>
                  )}
                  <div className="mt-3 text-[18px] font-semibold text-goldBright">
                    ${(p.cents / 100).toFixed(2)}
                  </div>
                  <div className="mt-1 text-[12px] text-stoneDim">
                    {busy === key ? "Opening checkout…" : "Tax at checkout"}
                  </div>
                </div>
              </GildedFrame>
            </button>
          );
        })}
      </div>
      <ErrorText>{err}</ErrorText>
      <Muted className="mt-5 text-[12px]">
        Payments handled by Stripe. Credits land on your account, so they work here, in the app, and on your phone.
      </Muted>
    </>
  );
}
